<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import FinestCard from '@/components/FinestCard.vue'
import ValueDisplay from '@/components/ValueDisplay.vue'
import EmptyState from '@/components/EmptyState.vue'
import SectionHeading from '@/components/SectionHeading.vue'
import PolicyList, { type PolicyRow } from '@/pages/banks/my-bank/PolicyList.vue'
import PartnerBankData = App.Data.PartnerBank.PartnerBankData
import PartnerBankNaturalPersonRuleData = App.Data.PartnerBank.PartnerBankNaturalPersonRuleData
import PartnerBankLegalEntityRuleData = App.Data.PartnerBank.PartnerBankLegalEntityRuleData

const props = defineProps<{
  partnerBank: PartnerBankData
}>()

const { t } = useI18n()

const profiles = computed(() => props.partnerBank.borrower_profiles ?? [])

const naturalPersonDecisions = (rule: PartnerBankNaturalPersonRuleData): PolicyRow[] => [
  { label: t('requires_permanent_employment'), decision: rule.requires_permanent_employment },
  { label: t('allows_probation_period'), decision: rule.allows_probation_period },
  { label: t('allows_eu_citizens'), decision: rule.allows_eu_citizens },
  { label: t('allows_non_eu_citizens'), decision: rule.allows_non_eu_citizens },
  { label: t('allows_swiss_residents'), decision: rule.allows_swiss_residents },
  {
    label: t('allows_non_german_citizens_with_german_residence'),
    decision: rule.allows_non_german_citizens_with_german_residence,
  },
  { label: t('requires_permanent_residence_permit'), decision: rule.requires_permanent_residence_permit },
  { label: t('allows_financing_past_retirement'), decision: rule.allows_financing_past_retirement },
]

const legalEntityDecisions = (rule: PartnerBankLegalEntityRuleData): PolicyRow[] => [
  { label: t('requires_registered_seat_in_germany'), decision: rule.requires_registered_seat_in_germany },
  { label: t('requires_short_balance_sheet_uploaded'), decision: rule.requires_short_balance_sheet_uploaded },
  { label: t('allows_newly_founded_entities'), decision: rule.allows_newly_founded_entities },
  { label: t('requires_ceo_eu_citizen'), decision: rule.requires_ceo_eu_citizen },
  { label: t('allows_non_german_minority_shareholders_eu'), decision: rule.allows_non_german_minority_shareholders_eu },
  {
    label: t('allows_non_german_minority_shareholders_world'),
    decision: rule.allows_non_german_minority_shareholders_world,
  },
  { label: t('allows_non_german_majority_shareholders_eu'), decision: rule.allows_non_german_majority_shareholders_eu },
  {
    label: t('allows_non_german_majority_shareholders_world'),
    decision: rule.allows_non_german_majority_shareholders_world,
  },
  {
    label: t('majority_shareholder_credit_check_sufficient'),
    decision: rule.majority_shareholder_credit_check_sufficient,
  },
]
</script>

<template>
  <EmptyState v-if="!profiles.length" :description="t('no_profiles')" />

  <div v-else class="flex flex-col gap-6">
    <FinestCard
      v-for="profile in profiles"
      :key="profile.id ?? profile.borrower_profile_type"
      :title="t(`profileType.${profile.borrower_profile_type}`)"
      icon="pi pi-id-card">
      <p v-if="profile.notes" class="text-surface-600 mb-2">{{ profile.notes }}</p>

      <template v-if="profile.natural_person_rule">
        <div class="grid gap-x-12 gap-y-6 sm:grid-cols-2 xl:grid-cols-3">
          <ValueDisplay
            :label="t('min_equity_percent')"
            :value="profile.natural_person_rule.min_equity_percent"
            format="number"
            suffix=" %" />
          <ValueDisplay
            :label="t('max_ltv_percent')"
            :value="profile.natural_person_rule.max_ltv_percent"
            format="number"
            suffix=" %" />
          <ValueDisplay
            :label="t('max_dti_factor')"
            :value="profile.natural_person_rule.max_dti_factor"
            format="number"
            suffix="x" />
          <ValueDisplay
            :label="t('min_dsti_percent')"
            :value="profile.natural_person_rule.min_dsti_percent"
            format="number"
            suffix=" %" />
          <ValueDisplay
            :label="t('min_household_surplus_amount')"
            :value="profile.natural_person_rule.min_household_surplus_amount"
            format="euros" />
          <ValueDisplay
            :label="t('credit_term')"
            :value="`${profile.natural_person_rule.min_credit_term_years ?? '–'}–${profile.natural_person_rule.max_credit_term_years ?? '–'} ${t('years')}`"
            format="string" />
          <ValueDisplay
            :label="t('existing_units')"
            :value="`${profile.natural_person_rule.min_existing_units ?? '–'}–${profile.natural_person_rule.max_existing_units ?? '–'}`"
            format="string" />
          <ValueDisplay
            :label="t('household_allowance_first_person')"
            :value="profile.natural_person_rule.household_allowance_first_person"
            format="euros" />
          <ValueDisplay
            :label="t('household_allowance_additional_adult')"
            :value="profile.natural_person_rule.household_allowance_additional_adult"
            format="euros" />
          <ValueDisplay
            :label="t('household_allowance_child')"
            :value="profile.natural_person_rule.household_allowance_child"
            format="euros" />
        </div>
        <SectionHeading>{{ t('conditions') }}</SectionHeading>
        <PolicyList class="mt-4" :items="naturalPersonDecisions(profile.natural_person_rule)" />
      </template>

      <template v-else-if="profile.legal_entity_rule">
        <div class="grid gap-x-12 gap-y-6 sm:grid-cols-2 xl:grid-cols-3">
          <ValueDisplay
            :label="t('min_equity_percent')"
            :value="profile.legal_entity_rule.min_equity_percent"
            format="number"
            suffix=" %" />
          <ValueDisplay
            :label="t('max_ltv_percent')"
            :value="profile.legal_entity_rule.max_ltv_percent"
            format="number"
            suffix=" %" />
          <ValueDisplay
            :label="t('max_dti_factor')"
            :value="profile.legal_entity_rule.max_dti_factor"
            format="number"
            suffix="x" />
          <ValueDisplay
            :label="t('min_dsti_percent')"
            :value="profile.legal_entity_rule.min_dsti_percent"
            format="number"
            suffix=" %" />
          <ValueDisplay
            :label="t('min_years_since_founding')"
            :value="profile.legal_entity_rule.min_years_since_founding"
            format="number"
            :suffix="` ${t('years')}`" />
          <ValueDisplay
            :label="t('credit_term')"
            :value="`${profile.legal_entity_rule.min_credit_term_years ?? '–'}–${profile.legal_entity_rule.max_credit_term_years ?? '–'} ${t('years')}`"
            format="string" />
          <ValueDisplay
            :label="t('existing_units')"
            :value="`${profile.legal_entity_rule.min_existing_units ?? '–'}–${profile.legal_entity_rule.max_existing_units ?? '–'}`"
            format="string" />
        </div>
        <SectionHeading>{{ t('conditions') }}</SectionHeading>
        <PolicyList class="mt-4" :items="legalEntityDecisions(profile.legal_entity_rule)" />
      </template>

      <EmptyState v-else :description="t('no_rules')" />
    </FinestCard>
  </div>
</template>

<i18n>
{
  "de": {
    "no_profiles": "Keine Kreditnehmerprofile hinterlegt.",
    "no_rules": "Für dieses Profil sind keine Detailregeln hinterlegt.",
    "conditions": "Bedingungen",
    "years": "Jahre",
    "min_equity_percent": "Mind. Eigenkapital",
    "max_ltv_percent": "Max. LTV",
    "max_dti_factor": "Max. DTI",
    "min_dsti_percent": "Mind. DSTI",
    "min_household_surplus_amount": "Mind. Haushaltsüberschuss",
    "min_years_since_founding": "Mind. Bestehensdauer",
    "credit_term": "Kreditlaufzeit",
    "existing_units": "Bestandseinheiten",
    "household_allowance_first_person": "Haushaltspauschale 1. Person",
    "household_allowance_additional_adult": "Pauschale weitere Erwachsene",
    "household_allowance_child": "Pauschale Kind",
    "requires_permanent_employment": "Unbefristete Anstellung erforderlich",
    "allows_probation_period": "Probezeit erlaubt",
    "allows_eu_citizens": "EU-Bürger erlaubt",
    "allows_non_eu_citizens": "Nicht-EU-Bürger erlaubt",
    "allows_swiss_residents": "Schweizer Ansässige erlaubt",
    "allows_non_german_citizens_with_german_residence": "Nicht-Deutsche mit dt. Wohnsitz erlaubt",
    "requires_permanent_residence_permit": "Unbefristete Aufenthaltserlaubnis erforderlich",
    "allows_financing_past_retirement": "Finanzierung über Renteneintritt hinaus",
    "requires_registered_seat_in_germany": "Sitz in Deutschland erforderlich",
    "requires_short_balance_sheet_uploaded": "Kurzbilanz erforderlich",
    "allows_newly_founded_entities": "Neugegründete Gesellschaften erlaubt",
    "requires_ceo_eu_citizen": "Geschäftsführer EU-Bürger erforderlich",
    "allows_non_german_minority_shareholders_eu": "Nicht-dt. Minderheitsges. (EU) erlaubt",
    "allows_non_german_minority_shareholders_world": "Nicht-dt. Minderheitsges. (weltweit) erlaubt",
    "allows_non_german_majority_shareholders_eu": "Nicht-dt. Mehrheitsges. (EU) erlaubt",
    "allows_non_german_majority_shareholders_world": "Nicht-dt. Mehrheitsges. (weltweit) erlaubt",
    "majority_shareholder_credit_check_sufficient": "Bonität Mehrheitsgesellschafter ausreichend",
    "profileType": {
      "natural_person": "Natürliche Person",
      "legal_entity": "Juristische Person",
      "gbr": "GbR",
      "freelancer": "Freiberufler"
    }
  },
  "en": {
    "no_profiles": "No borrower profiles set.",
    "no_rules": "No detailed rules set for this profile.",
    "conditions": "Conditions",
    "years": "years",
    "min_equity_percent": "Min. Equity",
    "max_ltv_percent": "Max. LTV",
    "max_dti_factor": "Max. DTI",
    "min_dsti_percent": "Min. DSTI",
    "min_household_surplus_amount": "Min. Household Surplus",
    "min_years_since_founding": "Min. Years Since Founding",
    "credit_term": "Credit Term",
    "existing_units": "Existing Units",
    "household_allowance_first_person": "Household Allowance 1st Person",
    "household_allowance_additional_adult": "Allowance Additional Adult",
    "household_allowance_child": "Allowance Child",
    "requires_permanent_employment": "Permanent Employment Required",
    "allows_probation_period": "Probation Period Allowed",
    "allows_eu_citizens": "EU Citizens Allowed",
    "allows_non_eu_citizens": "Non-EU Citizens Allowed",
    "allows_swiss_residents": "Swiss Residents Allowed",
    "allows_non_german_citizens_with_german_residence": "Non-German with German Residence Allowed",
    "requires_permanent_residence_permit": "Permanent Residence Permit Required",
    "allows_financing_past_retirement": "Financing Past Retirement",
    "requires_registered_seat_in_germany": "Registered Seat in Germany Required",
    "requires_short_balance_sheet_uploaded": "Short Balance Sheet Required",
    "allows_newly_founded_entities": "Newly Founded Entities Allowed",
    "requires_ceo_eu_citizen": "CEO EU Citizen Required",
    "allows_non_german_minority_shareholders_eu": "Non-German Minority Shareholders (EU) Allowed",
    "allows_non_german_minority_shareholders_world": "Non-German Minority Shareholders (World) Allowed",
    "allows_non_german_majority_shareholders_eu": "Non-German Majority Shareholders (EU) Allowed",
    "allows_non_german_majority_shareholders_world": "Non-German Majority Shareholders (World) Allowed",
    "majority_shareholder_credit_check_sufficient": "Majority Shareholder Credit Check Sufficient",
    "profileType": {
      "natural_person": "Natural Person",
      "legal_entity": "Legal Entity",
      "gbr": "GbR (Civil Law Partnership)",
      "freelancer": "Freelancer"
    }
  }
}
</i18n>
